import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import { signOut } from "~/lib/clerk";
import { type NavItem, navigationMenuQueryOptions } from "~/lib/query-options";
import ImageLogoHorizontalUrl from "~/img/logo/dcm-horizontal-1.png";
import LogoIcon from "~/img/logo/icon";
import { cn } from "~/lib/utils";
import { Button, ButtonLink } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";

import { ChevronDown, User } from "lucide-react";
import { Link } from "@tanstack/react-router";

// Recursive component for desktop navigation
const DesktopNavItem = ({ item }: { item: NavItem }) => {
  const hasChildren = item.data?.children && 0 < item.data.children.length;

  if (!hasChildren) {
    return (
      <a className="transition hover:text-black" href={item.data?.url || "#"}>
        {item.data?.label}
      </a>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1 transition hover:text-black">
        {item.data?.label}
        <ChevronDown className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {item.data?.children?.map((child) => (
          <DropdownMenuItem key={child.url} asChild>
            <a href={child.url || "#"}>{child.label}</a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Recursive component for mobile navigation
const MobileNavItem = ({ item }: { item: NavItem }) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const hasChildren = item.data?.children && 0 < item.data.children.length;

  return (
    <div className="w-full">
      {hasChildren ? (
        <div>
          <button
            onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
            className="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 flex w-full items-center justify-between rounded px-5 py-4 text-sm font-medium leading-none"
          >
            <span>{item.data?.label}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={cn(
                "transition-transform",
                isSubMenuOpen ? "rotate-180" : "",
              )}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <div
            className={cn(
              "overflow-hidden transition-all duration-300",
              isSubMenuOpen ? "max-h-96" : "max-h-0",
            )}
          >
            <div className="pl-4">
              {item.data?.children?.map((child) => (
                <a
                  key={child.url}
                  href={child.url || "#"}
                  className="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 block rounded px-5 py-3 text-sm font-medium leading-none"
                >
                  {child.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <a
          href={item.data?.url || "#"}
          className="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 block w-full rounded px-5 py-4 text-sm font-medium leading-none"
        >
          {item.data?.label}
        </a>
      )}
    </div>
  );
};

// User dropdown component
const UserDropdown = ({ onSignOut }: { onSignOut: () => void }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="text-brand-500 flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 transition hover:bg-brand-200 cursor-pointer active:bg-brand-300">
        <User className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/dashboard">Dashboard</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/coach-discovery">Coach Search</Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSignOut}>Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const { isSignedIn } = useAuth();
  const clerk = useClerk();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { data: navItems = [] } = useQuery(navigationMenuQueryOptions());

  const handleSignOut = async () => {
    try {
      if (!clerk) {
        return;
      }
      await signOut();
      await clerk.signOut();
    } catch {
      // Ignore sign out errors - user will be redirected anyway
    }
  };

  return (
    <div className="relative mx-auto w-full max-w-screen-xl">
      <div className="z-20 flex w-full flex-row items-center justify-between gap-4 px-4 py-4 xl:px-0">
        <div className="z-120 relative">
          <a href="/" className="flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-2">
              <img
                src={ImageLogoHorizontalUrl}
                className="hidden md:block md:h-[40px] md:w-[203px]"
                alt="DreamCoachMatch"
                decoding="sync"
                loading="eager"
              />
            </div>
            <LogoIcon className="h-12 w-12 md:hidden" />
            <span className="hidden pl-1 text-xs italic text-gray-500 md:block">
              The World's Coaching Marketplace
            </span>
          </a>
        </div>

        {/* Desktop Navigation */}
        <div className="text-md relative z-20 hidden grow items-center justify-end gap-8 rounded-full font-medium text-gray-800 md:flex">
          {navItems.map((link) => (
            <DesktopNavItem key={link.id} item={link} />
          ))}

          <ButtonLink href="/discovery/steps/1" size="sm">
            Find my dream coach
          </ButtonLink>

          {isSignedIn && <UserDropdown onSignOut={handleSignOut} />}
        </div>

        {/* Mobile Menu Button */}
        <Button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="z-120 absolute right-4 top-4 flex md:hidden"
          aria-label="Toggle menu"
          variant="ghost"
          size="icon-nav"
        >
          <div className="flex h-4 w-6 flex-col items-center justify-between">
            <span
              className={cn(
                "bg-brand-800 h-0.5 w-full rounded-full transition-transform",
                isMenuOpen && "translate-y-[7px] rotate-45",
              )}
            />
            <span
              className={cn(
                "bg-brand-800 h-0.5 w-full rounded-full transition-opacity",
                isMenuOpen && "opacity-0",
              )}
            />
            <span
              className={cn(
                "bg-brand-800 h-0.5 w-full rounded-full transition-transform",
                isMenuOpen && "-translate-y-[7px] -rotate-45",
              )}
            />
          </div>
        </Button>

        {/* Mobile Navigation */}
        <div
          className={cn(
            "z-100 fixed left-0 right-0 top-0 h-screen transition-all duration-300 md:hidden",
            isMenuOpen
              ? "pointer-events-auto bg-gray-900/80 backdrop-blur"
              : "pointer-events-none bg-transparent backdrop-blur-0",
          )}
          onClick={() => setIsMenuOpen(false)}
        >
          <div
            className={cn(
              "pointer-events-auto flex flex-col gap-2 rounded-b-2xl bg-white px-4 pb-8 pt-24 transition-all",
              isMenuOpen
                ? "translate-y-0 shadow-lg"
                : "-translate-y-full shadow-none",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {navItems.map((link) => (
              <MobileNavItem key={link.id} item={link} />
            ))}
            {isSignedIn && (
              <>
                <a
                  href="/settings"
                  className="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 w-full rounded px-5 py-4 text-sm font-medium leading-none"
                >
                  Preferences
                </a>
                <a
                  href="/coach-discovery"
                  className="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 w-full rounded px-5 py-4 text-sm font-medium leading-none"
                >
                  Coach Search
                </a>
                <button
                  onClick={handleSignOut}
                  className="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 w-full rounded px-5 py-4 text-left text-sm font-medium leading-none"
                >
                  Log out
                </button>
              </>
            )}

            <div className="ml-4">
              {!isSignedIn && (
                <ButtonLink href="/signin" size="sm">
                  Log in
                </ButtonLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
